import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import SubscriptionModal from '@/components/SubscriptionModal';
import { subscriptionModalManager } from '@/utils/subscriptionModal.utils';
import { useAuth } from './AuthProvider';
import { usePlans } from '@/features/plan/hooks/usePlanQuery';
import { useNavigate } from 'react-router-dom';

interface SubscriptionModalOptions {
  title?: string;
  message?: string;
  feature?: string;
  showClose?: boolean;
  onUpgrade?: () => void;
  onClose?: () => void;
  primaryActionLabel?: string;
  primaryActionType?: 'upgrade' | 'contact-support';
  chatbotId?:string;
}

interface SubscriptionModalContextType {
  showSubscriptionModal: (options?: SubscriptionModalOptions) => void;
  hideSubscriptionModal: () => void;
  isOpen: boolean;
}

const SubscriptionModalContext = createContext<SubscriptionModalContextType | undefined>(undefined);

export function useSubscriptionModal() {
  const context = useContext(SubscriptionModalContext);
  if (context === undefined) {
    throw new Error('useSubscriptionModal must be used within a SubscriptionModalProvider');
  }
  return context;
}

interface SubscriptionModalProviderProps {
  children: React.ReactNode;
}

export function SubscriptionModalProvider({ children }: SubscriptionModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState<SubscriptionModalOptions>({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: plansData } = usePlans();

  const plans = [...(plansData?.data?.subscriptions || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const highestPlan = plans[plans.length - 1];
  const isHighestPlanUser = Boolean(
    highestPlan &&
    user?.subscription?.id &&
    user.subscription.id === highestPlan._id
  );

  const showSubscriptionModal = useCallback((options: SubscriptionModalOptions = {}) => {
    setModalOptions(options);
    setIsOpen(true);
  }, []);

  const hideSubscriptionModal = useCallback(() => {
    setIsOpen(false);
    // Clear options after animation completes
    setTimeout(() => setModalOptions({}), 300);
  }, []);

  const handleClose = useCallback(() => {
    if (modalOptions.onClose) {
      modalOptions.onClose();
    }
    hideSubscriptionModal();
  }, [modalOptions, hideSubscriptionModal]);

  const handleUpgrade = useCallback(() => {
    if (modalOptions.onUpgrade) {
      modalOptions.onUpgrade();
    }
    hideSubscriptionModal();
  }, [modalOptions, hideSubscriptionModal]);

  const handlePrimaryAction = useCallback(() => {
    if (modalOptions.onUpgrade) {
      modalOptions.onUpgrade();
      hideSubscriptionModal();
      return;
    }

    const chatbotId = modalOptions.chatbotId;
    if (!chatbotId) {
      hideSubscriptionModal();
      return;
    }

    if (modalOptions.primaryActionType === 'contact-support' || isHighestPlanUser) {
      window.open(
        'https://wa.me/919209764995?text=Hi%20I%20need%20help%20with%20my%20plan%20limit.',
        '_blank'
      );
    } else {
      navigate(`/chatbot-builder/${chatbotId}/plans`);
    }

    hideSubscriptionModal();
  }, [modalOptions, hideSubscriptionModal, navigate, isHighestPlanUser]);

  // Subscribe to global event emitter
  useEffect(() => {
    const unsubscribe = subscriptionModalManager.subscribe((options) => {
      showSubscriptionModal(options);
    });
    return unsubscribe;
  }, [showSubscriptionModal]);

  const value: SubscriptionModalContextType = {
    showSubscriptionModal,
    hideSubscriptionModal,
    isOpen,
  };

  return (
    <SubscriptionModalContext.Provider value={value}>
      {children}
      <SubscriptionModal
        isOpen={isOpen}
        title={
          modalOptions.title ||
          (isHighestPlanUser ? 'Plan Limit Reached' : undefined)
        }
        message={
          modalOptions.message ||
          (isHighestPlanUser
            ? "You've reached the limit available on your current plan. You're already on the highest available plan. Contact support to discuss a higher limit."
            : undefined)
        }
        feature={modalOptions.feature}
        showClose={modalOptions.showClose}
        chatbotId={modalOptions.chatbotId!}
        onClose={handleClose}
        onUpgrade={modalOptions.onUpgrade ? handleUpgrade : undefined}
        onPrimaryAction={handlePrimaryAction}
        primaryActionLabel={
          modalOptions.primaryActionLabel ||
          (modalOptions.primaryActionType === 'contact-support' || isHighestPlanUser
            ? 'Contact Support'
            : 'Upgrade Now')
        }
        primaryActionType={
          modalOptions.primaryActionType ||
          (isHighestPlanUser ? 'contact-support' : 'upgrade')
        }
      />
    </SubscriptionModalContext.Provider>
  );
}
